// Demo Guest Portal - Mock Data
// Villa Ema, Živogošće (near Makarska), Croatia
// 2 apartments, group booking for 2 Polish families

import type { LocalRecommendation, LandlordService, SleepingConfig, UnitFacility, GuestHostUpdate } from '@/types/database';
import type { GuestPropertyAmenity } from '@/hooks/useAmenities';

// ============================================================
// IMAGES
// ============================================================

const IMG = '/demo';

export const DEMO_IMAGES = {
  // Apartman 1 - Biokovo (upper floor)
  unit1: [
    `${IMG}/a1-dnevni.jpg`,
    `${IMG}/a1-soba.jpg`,
    `${IMG}/a1-soba-2.jpg`,
    `${IMG}/a1-kuhinja.jpg`,
    `${IMG}/a1-kupatilo.jpg`,
    `${IMG}/a1-terasa.jpg`,
  ],
  // Apartman 2 - Jadran (ground floor)
  unit2: [
    `${IMG}/a2-dnevni.jpg`,
    `${IMG}/a2-soba.jpg`,
    `${IMG}/a2-soba-2.jpg`,
    `${IMG}/a2-kuhinja.jpg`,
    `${IMG}/a2-kupatilo.jpg`,
    `${IMG}/a2-terasa.png`,
  ],
  // Shared property images
  property: [
    `${IMG}/bazen-dan.jpg`,
    `${IMG}/bazen-noc.jpg`,
    `${IMG}/kuca-glavna.jpg`,
    `${IMG}/kuca-aerial.png`,
    `${IMG}/rostilj.jpg`,
    `${IMG}/parking.jpg`,
    `${IMG}/plaza-1.jpg`,
    `${IMG}/plaza-2.jpg`,
  ],
  cover: `${IMG}/bazen-dan.jpg`,
  surroundings: [
    `${IMG}/riva.jpg`,
    `${IMG}/trgovina.jpg`,
  ],
} as const;

// Categorized images for gallery with tabs
export interface DemoCategoryImage {
  url: string;
  category: string;
  label: { hr: string; en: string };
}

export const DEMO_CATEGORIZED_IMAGES = {
  unit1: [
    { url: `${IMG}/a1-dnevni.jpg`, category: 'accommodation', label: { hr: 'Dnevni boravak', en: 'Living room' } },
    { url: `${IMG}/a1-soba.jpg`, category: 'accommodation', label: { hr: 'Spavaća soba', en: 'Bedroom' } },
    { url: `${IMG}/a1-soba-2.jpg`, category: 'accommodation', label: { hr: 'Druga soba', en: 'Second room' } },
    { url: `${IMG}/a1-kuhinja.jpg`, category: 'kitchen', label: { hr: 'Kuhinja', en: 'Kitchen' } },
    { url: `${IMG}/a1-kupatilo.jpg`, category: 'bathroom', label: { hr: 'Kupaonica', en: 'Bathroom' } },
    { url: `${IMG}/a1-terasa.jpg`, category: 'terrace', label: { hr: 'Balkon s pogledom', en: 'Balcony with view' } },
  ] as DemoCategoryImage[],
  unit2: [
    { url: `${IMG}/a2-dnevni.jpg`, category: 'accommodation', label: { hr: 'Dnevni boravak', en: 'Living room' } },
    { url: `${IMG}/a2-soba.jpg`, category: 'accommodation', label: { hr: 'Spavaća soba', en: 'Bedroom' } },
    { url: `${IMG}/a2-soba-2.jpg`, category: 'accommodation', label: { hr: 'Druga soba', en: 'Second room' } },
    { url: `${IMG}/a2-kuhinja.jpg`, category: 'kitchen', label: { hr: 'Kuhinja', en: 'Kitchen' } },
    { url: `${IMG}/a2-kupatilo.jpg`, category: 'bathroom', label: { hr: 'Kupaonica', en: 'Bathroom' } },
    { url: `${IMG}/a2-terasa.png`, category: 'terrace', label: { hr: 'Terasa s pogledom', en: 'Terrace with view' } },
  ] as DemoCategoryImage[],
  property: [
    { url: `${IMG}/bazen-dan.jpg`, category: 'pool', label: { hr: 'Bazen po danu', en: 'Pool by day' } },
    { url: `${IMG}/bazen-noc.jpg`, category: 'pool', label: { hr: 'Bazen po noći', en: 'Pool at night' } },
    { url: `${IMG}/kuca-glavna.jpg`, category: 'exterior', label: { hr: 'Kuća - glavni pogled', en: 'House - main view' } },
    { url: `${IMG}/kuca-aerial.png`, category: 'exterior', label: { hr: 'Pogled iz zraka', en: 'Aerial view' } },
    { url: `${IMG}/rostilj.jpg`, category: 'exterior', label: { hr: 'Roštilj', en: 'BBQ area' } },
    { url: `${IMG}/parking.jpg`, category: 'parking', label: { hr: 'Parking', en: 'Parking' } },
    { url: `${IMG}/plaza-1.jpg`, category: 'beach', label: { hr: 'Plaža', en: 'Beach' } },
    { url: `${IMG}/plaza-2.jpg`, category: 'beach', label: { hr: 'Plaža i more', en: 'Beach and sea' } },
    { url: `${IMG}/riva.jpg`, category: 'surroundings', label: { hr: 'Makarska riva', en: 'Makarska promenade' } },
    { url: `${IMG}/trgovina.jpg`, category: 'surroundings', label: { hr: 'Lokalna trgovina', en: 'Local shop' } },
  ] as DemoCategoryImage[],
};

// ============================================================
// PROPERTY & LANDLORD
// ============================================================

export const DEMO_PROPERTY = {
  id: 'demo-property-001',
  name: 'Villa Ema',
  address: 'Put Živogošća 15',
  city: 'Živogošće',
  country: 'Hrvatska',
  latitude: 43.1878,
  longitude: 17.1625,
  description: {
    hr: 'Kamena dalmatinska kuća iz 19. stoljeća, potpuno renovirana, smještena direktno na šljunčanoj plaži s pogledom na planinu Biokovo. Vila ima dva zasebna apartmana, zajednički bazen, roštilj i privatni parking. Idealna za obitelji s djecom - plaža je doslovno ispred kuće s plitkim i mirnim morem.',
    en: '19th century Dalmatian stone house, fully renovated, located directly on the pebble beach with stunning views of Biokovo mountain. The villa features two separate apartments, a shared pool, BBQ area, and private parking. Perfect for families with children - the beach is literally in front of the house with shallow, calm waters.',
  },
};

export const DEMO_LANDLORD = {
  firstName: 'Ema',
  lastName: 'Jurić',
  phone: '+385 21 678 901',
  email: 'ema@villaema.hr',
  avatarUrl: null as string | null,
};

// ============================================================
// UNITS
// ============================================================

export const DEMO_UNITS = {
  unit1: {
    id: 'demo-unit-001',
    name: {
      hr: 'Apartman Biokovo',
      en: 'Apartment Biokovo',
    },
    description: {
      hr: 'Apartman na gornjem katu kamene kuće s pogledom na planinu Biokovo i more. Prostrani dnevni boravak s kamenim zidovima i kaminom, potpuno opremljena kuhinja, spavaća soba s bračnim krevetom i kupaonica s tušem. Balkon s pogledom na more i plaža ispod kuće.',
      en: 'Upper floor apartment in a stone house with views of Biokovo mountain and the sea. Spacious living room with stone walls and fireplace, fully equipped kitchen, bedroom with double bed, and bathroom with shower. Balcony with sea view and beach below the house.',
    },
    bedrooms: 2,
    bathrooms: 1,
    maxGuests: 4,
    sizeSqm: 55,
    basePrice: 200,
    images: DEMO_IMAGES.unit1,
    amenities: ['wifi', 'ac', 'tv', 'kitchen', 'dishwasher', 'washing_machine', 'iron', 'hair_dryer', 'safe', 'balcony'],
  },
  unit2: {
    id: 'demo-unit-002',
    name: {
      hr: 'Apartman Jadran',
      en: 'Apartment Jadran',
    },
    description: {
      hr: 'Apartman u prizemlju s direktnim izlazom na plažu. Dnevni boravak s kamenim zidovima i pogledom na more, kuhinja s blagovaonicom, spavaća soba i kupaonica. Prostrana terasa s pogledom na more i Biokovo - savršeno za jutarnju kavu.',
      en: 'Ground floor apartment with direct beach access. Living room with stone walls and sea view, kitchen with dining area, bedroom and bathroom. Spacious terrace overlooking the sea and Biokovo - perfect for morning coffee.',
    },
    bedrooms: 2,
    bathrooms: 1,
    maxGuests: 4,
    sizeSqm: 50,
    basePrice: 200,
    images: DEMO_IMAGES.unit2,
    amenities: ['wifi', 'ac', 'tv', 'kitchen', 'dishwasher', 'washing_machine', 'iron', 'hair_dryer', 'safe', 'terrace'],
  },
} as const;

// ============================================================
// SLEEPING CONFIG
// ============================================================

export const DEMO_SLEEPING_UNIT1: SleepingConfig = {
  rooms: [
    {
      id: 'room-u1-1',
      type: 'bedroom',
      name: 'Spavaća soba',
      beds: [{ type: 'queen', count: 1 }],
    },
    {
      id: 'room-u1-2',
      type: 'living_room',
      name: 'Dnevni boravak',
      beds: [{ type: 'sofa_bed', count: 1 }],
    },
  ],
  extraBeds: { available: false },
  totalCapacity: 4,
};

export const DEMO_SLEEPING_UNIT2: SleepingConfig = {
  rooms: [
    {
      id: 'room-u2-1',
      type: 'bedroom',
      name: 'Spavaća soba',
      beds: [{ type: 'queen', count: 1 }],
    },
    {
      id: 'room-u2-2',
      type: 'living_room',
      name: 'Dnevni boravak',
      beds: [{ type: 'sofa_bed', count: 1 }],
    },
  ],
  extraBeds: { available: true, types: ['crib'], count: 1, price: 0, notes: 'Krevetić za bebe na upit / Baby crib available on request' },
  totalCapacity: 4,
};

// ============================================================
// BOOKING GROUP
// ============================================================

export const DEMO_GROUP = {
  name: 'Rodzina Kowalski & Nowak',
  guestName: 'Agnieszka Kowalska',
  guestPhone: '+48 512 345 678',
  guestEmail: 'agnieszka.kowalska@gmail.com',
  totalGuests: 8,
  checkIn: '2026-07-12',
  checkOut: '2026-07-19',
  totalPrice: 2800,
  totalPaid: 1400,
  source: 'booking',
  bookings: [
    {
      id: 'demo-booking-001',
      unitId: 'demo-unit-001',
      guestsCount: 4,
      adultsCount: 2,
      childrenCount: 2,
      infantsCount: 0,
      totalPrice: 1400,
      amountPaid: 700,
    },
    {
      id: 'demo-booking-002',
      unitId: 'demo-unit-002',
      guestsCount: 4,
      adultsCount: 2,
      childrenCount: 1,
      infantsCount: 1,
      totalPrice: 1400,
      amountPaid: 700,
    },
  ],
};

// Unit booking info for UnitSwitcher
export const DEMO_UNIT_BOOKINGS = [
  {
    bookingId: 'demo-booking-001',
    unitId: 'demo-unit-001',
    unitName: 'Apartman Biokovo',
    checkIn: '2026-07-12',
    checkOut: '2026-07-19',
    guestsCount: 4,
    adultsCount: 2,
    childrenCount: 2,
    infantsCount: 0,
  },
  {
    bookingId: 'demo-booking-002',
    unitId: 'demo-unit-002',
    unitName: 'Apartman Jadran',
    checkIn: '2026-07-12',
    checkOut: '2026-07-19',
    guestsCount: 4,
    adultsCount: 2,
    childrenCount: 1,
    infantsCount: 1,
  },
];

// ============================================================
// WIFI, PARKING, SELF CHECK-IN, HOUSE RULES
// ============================================================

export const DEMO_WIFI = {
  networkName: 'VillaEma_5G',
  password: 'Makarska2026!',
};

export const DEMO_PARKING = {
  available: true,
  type: 'private' as const,
  free: true,
  pricePerDay: null as number | null,
  latitude: 43.1882,
  longitude: 17.1620,
  address: 'Put Živogošća 15a, Živogošće',
  instructions: {
    hr: 'Privatni parking je 50m iznad kuće. Slijedite cestu uzbrdo i skrenite desno na makadamski put. Parking je na ravnoj površini s maslinicima.',
    en: 'Private parking is 50m above the house. Follow the road uphill and turn right onto the gravel path. Parking is on a flat area among olive groves.',
  },
  afterArrival: {
    hr: 'Nakon parkiranja, pješke se spustite niz kamene stepenice do kuće (2 minute). Pratite oznake "Villa Ema".',
    en: 'After parking, walk down the stone steps to the house (2 minutes). Follow the "Villa Ema" signs.',
  },
  spots: 4,
  vehicleRestrictions: null as string | null,
  notes: null as string | null,
};

export const DEMO_SELF_CHECKIN = {
  enabled: true,
  method: 'lockbox' as const,
  lockboxLocation: {
    hr: 'Lockbox je na kamenom zidu lijevo od ulaznih vrata.',
    en: 'The lockbox is on the stone wall to the left of the entrance door.',
  },
  lockboxCode: '4523',
};

export const DEMO_HOUSE_RULES = {
  hr: `• Zabranjeno pušenje unutar apartmana
• Tišina od 23:00 do 07:00
• Bazen otvoreni: 08:00 - 22:00 (djeca pod nadzorom)
• Bez stakla oko bazena
• Obavezno čišćenje roštilja nakon korištenja
• Mali kućni ljubimci dobrodošli uz prethodni dogovor
• Smeće se odnosi u kontejner na 100m (utorak i petak)`,
  en: `• No smoking inside the apartment
• Quiet hours: 23:00 - 07:00
• Pool open: 08:00 - 22:00 (children must be supervised)
• No glass around the pool area
• Please clean the BBQ after use
• Small pets welcome with prior arrangement
• Garbage collection point is 100m away (Tuesday and Friday)`,
};

export const DEMO_CHECK_TIMES = {
  checkInTime: '15:00',
  checkOutTime: '10:00',
};

// ============================================================
// SHARED AMENITIES (Property-level)
// ============================================================

export const DEMO_SHARED_AMENITIES: GuestPropertyAmenity[] = [
  { amenity_id: 'pool', category: 'outdoor', subcategory: null, icon: 'Waves', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: '08:00 - 22:00', seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: null, custom_value: null, is_highlighted: true },
  { amenity_id: 'barbecue', category: 'outdoor', subcategory: null, icon: 'Flame', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: null, custom_value: null, is_highlighted: true },
  { amenity_id: 'parking', category: 'outdoor', subcategory: null, icon: 'Car', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: '4 mjesta / 4 spots', custom_value: null, is_highlighted: false },
  { amenity_id: 'playground', category: 'outdoor', subcategory: null, icon: 'Baby', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: null, custom_value: null, is_highlighted: false },
  { amenity_id: 'playstation', category: 'entertainment', subcategory: null, icon: 'Gamepad2', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: 'PlayStation 5 + 4 controllera', custom_value: null, is_highlighted: false },
  { amenity_id: 'board_games', category: 'entertainment', subcategory: null, icon: 'Dices', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: null, custom_value: null, is_highlighted: false },
  { amenity_id: 'beach_access', category: 'outdoor', subcategory: null, icon: 'Umbrella', access_type: 'shared', access_status: 'included', restrictions: null, operating_hours: null, seasonal_period: null, reservation_required: false, fee: null, fee_type: null, notes: null, custom_value: null, is_highlighted: true },
];

// ============================================================
// CHAT MESSAGES (HR ↔ PL translation demo)
// ============================================================

export interface DemoChatMessage {
  id: string;
  senderType: 'landlord' | 'guest';
  originalContent: string;
  originalLanguage: string;
  translatedContent: string;
  translatedLanguage: string;
  timestamp: string;
  isRead: boolean;
}

export const DEMO_CHAT_MESSAGES: DemoChatMessage[] = [
  {
    id: 'msg-1',
    senderType: 'landlord',
    originalContent: 'Bok Agnieszka! Dobrodošli u Villu Ema. Ako trebate bilo što, slobodno pitajte!',
    originalLanguage: 'hr',
    translatedContent: 'Cześć Agnieszka! Witamy w Villa Ema. Jeśli czegoś potrzebujecie, śmiało pytajcie!',
    translatedLanguage: 'pl',
    timestamp: '2026-07-10T14:30:00Z',
    isRead: true,
  },
  {
    id: 'msg-2',
    senderType: 'guest',
    originalContent: 'Dziękujemy Ema! Czy parking jest blisko domu?',
    originalLanguage: 'pl',
    translatedContent: 'Hvala Ema! Je li parking blizu kuće?',
    translatedLanguage: 'hr',
    timestamp: '2026-07-10T14:45:00Z',
    isRead: true,
  },
  {
    id: 'msg-3',
    senderType: 'landlord',
    originalContent: 'Da, privatni parking je odmah iznad kuće, 50m pješke. Ima mjesta za 4 auta.',
    originalLanguage: 'hr',
    translatedContent: 'Tak, prywatny parking jest tuż nad domem, 50m pieszo. Są miejsca na 4 samochody.',
    translatedLanguage: 'pl',
    timestamp: '2026-07-10T14:48:00Z',
    isRead: true,
  },
  {
    id: 'msg-4',
    senderType: 'guest',
    originalContent: 'Super! A gdzie jest najbliższa plaża?',
    originalLanguage: 'pl',
    translatedContent: 'Super! A gdje je najbliža plaža?',
    translatedLanguage: 'hr',
    timestamp: '2026-07-10T15:02:00Z',
    isRead: true,
  },
  {
    id: 'msg-5',
    senderType: 'landlord',
    originalContent: 'Plaža je doslovno ispred kuće! Samo siđete stepenicama i tu ste. Šljunčana je i plitko more - savršeno za djecu.',
    originalLanguage: 'hr',
    translatedContent: 'Plaża jest dosłownie przed domem! Wystarczy zejść po schodach. Żwirowa z płytkim morzem - idealna dla dzieci.',
    translatedLanguage: 'pl',
    timestamp: '2026-07-10T15:05:00Z',
    isRead: true,
  },
  {
    id: 'msg-6',
    senderType: 'guest',
    originalContent: 'Wspaniale! Nie możemy się doczekać!',
    originalLanguage: 'pl',
    translatedContent: 'Divno! Jedva čekamo!',
    translatedLanguage: 'hr',
    timestamp: '2026-07-10T15:10:00Z',
    isRead: true,
  },
  {
    id: 'msg-7',
    senderType: 'landlord',
    originalContent: 'Preporučujem restoran Jeny za večeru, 3 min pješke. Imaju odličan crni rižoto!',
    originalLanguage: 'hr',
    translatedContent: 'Polecam restaurację Jeny na kolację, 3 min pieszo. Mają świetne czarne risotto!',
    translatedLanguage: 'pl',
    timestamp: '2026-07-10T15:15:00Z',
    isRead: true,
  },
];

// ============================================================
// LOCAL RECOMMENDATIONS
// ============================================================

const now = new Date().toISOString();

export const DEMO_RECOMMENDATIONS: LocalRecommendation[] = [
  {
    id: 'rec-1', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Restoran Jeny', category: 'restaurant', subcategory: null,
    description: 'Obiteljski restoran s najboljim crnim rižotom na rivijeri. Svježa riba svaki dan.',
    address: 'Živogošće bb', city: 'Živogošće',
    latitude: 43.1875, longitude: 17.1632, distance_km: 0.3,
    phone: '+385 21 627 000', website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: true, personal_note: 'Naš omiljeni restoran! Crni rižoto je obavezan.',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 1, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: 'Rezervirajte stol unaprijed u sezoni, posebno za večeru.',
  },
  {
    id: 'rec-2', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Konoba Kalalarga', category: 'restaurant', subcategory: null,
    description: 'Tradicionalna dalmatinska konoba u centru Makarske. Pašticada, janjetina i domaći pršut.',
    address: 'Kalalarga 10', city: 'Makarska',
    latitude: 43.2969, longitude: 17.0189, distance_km: 15,
    phone: '+385 21 611 511', website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: true, personal_note: 'Autentična dalmatinska kuhinja, topla atmosfera.',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 2, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
  {
    id: 'rec-3', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Beach Bar Palma', category: 'bar', subcategory: null,
    description: 'Opušteni beach bar s koktelima i laganom hranom direktno na plaži.',
    address: 'Živogošće bb', city: 'Živogošće',
    latitude: 43.1872, longitude: 17.1630, distance_km: 0.2,
    phone: null, website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: 4, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 3, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
  {
    id: 'rec-4', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Plaža Živogošće', category: 'beach', subcategory: null,
    description: 'Prekrasna šljunčana plaža s kristalno čistim morem. Plitko more idealno za djecu.',
    address: 'Živogošće', city: 'Živogošće',
    latitude: 43.1876, longitude: 17.1627, distance_km: 0.05,
    phone: null, website: null, google_maps_url: null,
    image_url: `${IMG}/plaza-1.jpg`, images: [`${IMG}/plaza-1.jpg`, `${IMG}/plaza-2.jpg`],
    is_personal_recommendation: true, personal_note: 'Naša plaža - doslovno ispred kuće!',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 4, is_active: true, created_at: now, updated_at: now,
    beach_type: 'pebble', beach_facilities: ['sunbeds', 'showers'], suitable_for: ['families', 'children', 'water_sports'], water_depth: 'shallow', entry_type: 'gradual',
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: 'Samo siđite stepenicama ispred kuće!', insider_tip: 'Ujutro je plaža gotovo prazna - savršeno za kupanje.',
  },
  {
    id: 'rec-5', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Plaža Nugal', category: 'beach', subcategory: null,
    description: 'Skrivena plaža okružena liticama. Jedna od najljepših plaža u Hrvatskoj.',
    address: 'Nugal', city: 'Makarska',
    latitude: 43.2850, longitude: 17.0100, distance_km: 12,
    phone: null, website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: true, personal_note: 'Morate posjetiti! 15 min šetnje od parkinga.',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 5, is_active: true, created_at: now, updated_at: now,
    beach_type: 'pebble', beach_facilities: [], suitable_for: ['nudist', 'snorkeling'], water_depth: 'deep', entry_type: 'rocky',
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: 'Autom do Makarske, zatim 15 min pješke stazom kroz borovu šumu.', insider_tip: 'Ponesite vode i obuću za hodanje - staza je kamenita.',
  },
  {
    id: 'rec-6', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Biokovo Nature Park', category: 'attraction', subcategory: null,
    description: 'Nacionalni park s legendarnim Skywalk vidikovcem na 1228m nadmorske visine.',
    address: 'PP Biokovo', city: 'Makarska',
    latitude: 43.3200, longitude: 17.0500, distance_km: 20,
    phone: '+385 21 616 924', website: 'https://pp-biokovo.hr', google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: true, personal_note: 'Skywalk je nezaboravan doživljaj! Pogled na otoke je nevjerojatan.',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 6, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: 'easy', duration_hours: 3, price_info: '50 kn ulaznica', gpx_url: null, schedule_url: null,
    how_to_get_there: 'Autom 30 min od Živogošća, serpentine do vrha.', insider_tip: 'Idite ujutro za bolji pogled (manje oblaka). Karte kupite online.',
  },
  {
    id: 'rec-7', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Makarska Riva', category: 'attraction', subcategory: null,
    description: 'Poznata šetnica uz more s kafićima, sladoledima i pogledom na otoke.',
    address: 'Obala kralja Tomislava', city: 'Makarska',
    latitude: 43.2971, longitude: 17.0178, distance_km: 15,
    phone: null, website: null, google_maps_url: null,
    image_url: `${IMG}/riva.jpg`, images: [`${IMG}/riva.jpg`],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: 4, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 7, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
  {
    id: 'rec-8', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Ljekarna Makarska', category: 'pharmacy', subcategory: null,
    description: 'Najbliža ljekarna, otvorena svaki dan.',
    address: 'Marineta 2', city: 'Makarska',
    latitude: 43.2975, longitude: 17.0185, distance_km: 15,
    phone: '+385 21 611 268', website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: null, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 8, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
  {
    id: 'rec-9', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Konzum', category: 'shop', subcategory: null,
    description: 'Najbliža veća trgovina s osnovnim potrepštinama.',
    address: 'Drvenik', city: 'Drvenik',
    latitude: 43.1750, longitude: 17.1580, distance_km: 2,
    phone: null, website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: null, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 9, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: null, gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: 'Mala trgovina u selu je bliže (100m) za kruh i mlijeko.',
  },
  {
    id: 'rec-10', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Aquapark Makarska', category: 'attraction', subcategory: null,
    description: 'Zabavni vodeni park s toboganima i bazenom. Idealno za djecu!',
    address: 'Makarska', city: 'Makarska',
    latitude: 43.2900, longitude: 17.0200, distance_km: 14,
    phone: null, website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: 4, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 10, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: 4, price_info: '15 EUR odrasli, 10 EUR djeca', gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
  {
    id: 'rec-11', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Rafting Cetina', category: 'excursion', subcategory: null,
    description: 'Adrenalinski rafting na rijeci Cetini. 3-satna tura kroz kanjon s vodičem.',
    address: 'Omiš', city: 'Omiš',
    latitude: 43.4447, longitude: 16.6933, distance_km: 55,
    phone: '+385 98 123 456', website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: true, personal_note: 'Obavezno za ljubitelje adrenalina! Djeca od 7 godina mogu sudjelovati.',
    landlord_rating: 5, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 11, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: 'moderate', duration_hours: 3, price_info: '35 EUR/osoba', gpx_url: null, schedule_url: null,
    how_to_get_there: 'Autom 45 min prema Omišu. Organizatori nude i prijevoz.', insider_tip: null,
  },
  {
    id: 'rec-12', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Rent-a-bike Živogošće', category: 'rental_sea', subcategory: null,
    description: 'Najam bicikala, kajaka i SUP dasaka. U selu, 5 min pješke.',
    address: 'Živogošće', city: 'Živogošće',
    latitude: 43.1870, longitude: 17.1635, distance_km: 0.5,
    phone: '+385 99 876 543', website: null, google_maps_url: null,
    image_url: null, images: [],
    is_personal_recommendation: false, personal_note: null,
    landlord_rating: null, is_ai_suggested: false, ai_source: null, ai_place_id: null,
    display_order: 12, is_active: true, created_at: now, updated_at: now,
    beach_type: null, beach_facilities: [], suitable_for: [], water_depth: null, entry_type: null,
    event_date: null, event_time: null, event_end_date: null, is_recurring: false, recurrence_day: null,
    difficulty: null, duration_hours: null, price_info: '10 EUR/dan bicikl, 15 EUR/sat kajak', gpx_url: null, schedule_url: null,
    how_to_get_there: null, insider_tip: null,
  },
];

// Personal recommendations (for HostCornerTab)
export const DEMO_PERSONAL_RECOMMENDATIONS = DEMO_RECOMMENDATIONS.filter(r => r.is_personal_recommendation);

// ============================================================
// LANDLORD SERVICES
// ============================================================

export const DEMO_SERVICES: LandlordService[] = [
  {
    id: 'svc-1', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Domaće maslinovo ulje',
    description: 'Ekstra djevičansko maslinovo ulje s naših 200-godišnjih maslina. Hladno prešano, nefiltrirano.',
    service_type: 'product', price: 15, price_type: 'fixed', currency: 'EUR',
    image_url: null, images: [], document_url: null, document_name: null,
    is_available: true, quantity: 10, display_order: 1, is_active: true,
    created_at: now, updated_at: now,
  },
  {
    id: 'svc-2', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Ležaljka za plažu',
    description: 'Udobna ležaljka sa suncobranom za plažu ispred kuće.',
    service_type: 'rental', price: 5, price_type: 'per_day', currency: 'EUR',
    image_url: null, images: [], document_url: null, document_name: null,
    is_available: true, quantity: 6, display_order: 2, is_active: true,
    created_at: now, updated_at: now,
  },
  {
    id: 'svc-3', property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Vođena kajak tura',
    description: '2-satna kajak tura do skrivenih uvala uz obalu. Uključena oprema i vodič.',
    service_type: 'service', price: 25, price_type: 'per_person', currency: 'EUR',
    image_url: null, images: [], document_url: null, document_name: null,
    is_available: true, quantity: 8, display_order: 3, is_active: true,
    created_at: now, updated_at: now,
  },
];

// ============================================================
// FACILITIES (pool, BBQ rules)
// ============================================================

export const DEMO_FACILITIES: UnitFacility[] = [
  {
    id: 'fac-1', unit_id: DEMO_UNITS.unit1.id, property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Bazen', facility_type: 'pool',
    description: 'Vanjski bazen 8x4m s morskom vodom. Dubina 1.2m - 1.8m.',
    location_description: 'Između kuće i plaže, s pogledom na more.',
    usage_rules: 'Otvoreno 08:00-22:00. Djeca moraju biti pod nadzorom. Zabranjena stakla oko bazena. Tuširajte se prije ulaska.',
    opening_hours: '08:00 - 22:00', requires_booking: false, max_users: 10,
    image_url: `${IMG}/bazen-dan.jpg`, images: [`${IMG}/bazen-dan.jpg`, `${IMG}/bazen-noc.jpg`],
    display_order: 1, is_active: true, created_at: now, updated_at: now,
  },
  {
    id: 'fac-2', unit_id: DEMO_UNITS.unit1.id, property_id: DEMO_PROPERTY.id, landlord_id: 'demo-landlord',
    name: 'Roštilj', facility_type: 'bbq',
    description: 'Kameni roštilj s klupama i pogledom na more.',
    location_description: 'Na terasi iznad parkinga.',
    usage_rules: 'Slobodno koristite drva iz spremišta. Obavezno očistite nakon korištenja. Pepeo bacite u kantu za pepeo.',
    opening_hours: null, requires_booking: false, max_users: null,
    image_url: `${IMG}/rostilj.jpg`, images: [`${IMG}/rostilj.jpg`],
    display_order: 2, is_active: true, created_at: now, updated_at: now,
  },
];

// ============================================================
// EQUIPMENT (unit-level: TV, AC, kitchen, etc.)
// ============================================================

export const DEMO_EQUIPMENT = {
  tv_available: true,
  tv_instructions: 'Samsung Smart TV. Daljinski je na polici ispod TV-a.',
  tv_channels: 'HRT, RTL, Nova TV, Sport Klub, HBO',
  streaming_services: [
    { name: 'Netflix', instructions: 'Profil "Gosti" - ne trebate lozinku' },
  ],
  ac_available: true,
  ac_instructions: 'Daikin klima uređaj. Daljinski na zidu pokraj ulaza. Preporučena temperatura 24°C.',
  ac_rules: 'Molimo zatvorite prozore dok je klima uključena radi uštede energije.',
  kitchen_type: 'full',
  stove_type: 'electric',
  kitchen_instructions: 'Potpuno opremljena kuhinja: lonci, tave, pribor za jelo, čaše, šalice. Perilica posuđa ispod radne ploče.',
  dishwasher_available: true,
  dishwasher_instructions: 'Tablete su u ladici ispod sudopera.',
  washing_machine_available: true,
  washing_machine_instructions: 'Perilica rublja u kupaonici. Deterdžent na polici iznad.',
  trash_instructions: 'Komunalni otpad: siva kanta (mješoviti), zelena kanta (plastika), žuta kanta (papir).',
  trash_collection_days: 'Utorak i petak ujutro',
  first_aid_location: 'Kutija prve pomoći u ormariću ispod ogledala u kupaonici.',
};

// ============================================================
// HOST UPDATES (landlord posts for guests)
// ============================================================

export const DEMO_HOST_UPDATES: GuestHostUpdate[] = [
  {
    id: 'upd-1',
    image_url: `${IMG}/jutro-bazen.jpg`,
    caption: 'Dobro jutro iz Ville Ema! ☀️ Bazen je spreman, jutarnje kupanje uz kavu i pogled na more - ovako počinje savršen dan u Dalmaciji.',
    duration_type: '24h',
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    expires_at: null,
  },
  {
    id: 'upd-2',
    image_url: `${IMG}/riba-gradele.jpg`,
    caption: 'Svježa riba s gradela! 🐟 Upravo stigla od lokalnog ribara - ako želite, večeras pripremamo tradicionalnu dalmatinsku večeru na zajedničkom roštilju.',
    duration_type: '24h',
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    expires_at: null,
  },
  {
    id: 'upd-3',
    image_url: `${IMG}/rostilj.jpg`,
    caption: 'Roštilj je spreman za korištenje! Drva su u spremištu pokraj parkinga.',
    duration_type: 'permanent',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    expires_at: null,
  },
];

// ============================================================
// MOCK WEATHER DATA
// ============================================================

export const DEMO_WEATHER = {
  current: {
    temperature: 28,
    humidity: 65,
    windSpeed: 12,
    uvIndex: 7,
    description: { hr: 'Sunčano', en: 'Sunny' },
    icon: '☀️',
  },
  forecast: [
    { day: { hr: 'Pon', en: 'Mon' }, high: 29, low: 22, icon: '☀️' },
    { day: { hr: 'Uto', en: 'Tue' }, high: 30, low: 23, icon: '☀️' },
    { day: { hr: 'Sri', en: 'Wed' }, high: 28, low: 21, icon: '⛅' },
    { day: { hr: 'Čet', en: 'Thu' }, high: 27, low: 20, icon: '🌤️' },
    { day: { hr: 'Pet', en: 'Fri' }, high: 31, low: 24, icon: '☀️' },
  ],
};

// ============================================================
// HELPER: Get localized content
// ============================================================

export function getLocalizedText(text: { hr: string; en: string }, language: string): string {
  return language === 'hr' ? text.hr : text.en;
}
